import { useLocation } from 'react-router-dom';
import { useClients } from '@/hooks/useClients';

/**
 * Detects the current page context for Ask Poco auto-scoping.
 *
 * When the user is on a client detail page (`/clients/:id/*`), returns
 * the matching client ID and name so Ask Poco can pre-scope to that client.
 * On all other pages returns a global scope with no client binding.
 *
 * @returns An object containing:
 *   - `clientId` — the UUID of the current client (only when on a client page)
 *   - `clientName` — the display name of the current client (only when found in cache)
 *   - `scope` — `'client'` when on a client page, `'global'` otherwise
 */
export function usePageContext(): {
  clientId?: string;
  clientName?: string;
  scope: 'client' | 'global';
} {
  const { pathname } = useLocation();
  const { clients } = useClients();

  const match = pathname.match(/\/clients\/([^/]+)/);
  if (match) {
    const clientId = match[1];
    const client = clients.find((c) => c.id === clientId);
    return {
      clientId,
      clientName: client?.name,
      scope: 'client',
    };
  }

  return { scope: 'global' };
}
